import { Skeleton } from '@/components/loading/Skeleton'
import { useEffect, useRef, type ReactElement } from 'react'
import { Check, RotateCw, Video, VideoOff } from 'lucide-react'
import { Badge, type BadgeTone } from '@/components/play'
import { StreamVideoSurface } from '@/components/livekit/StreamVideoSurface'
import { useEventLiveKitLivePublisherIdentities } from '@/components/livekit/EventLiveKitProvider'
import { CAMERA_STATE_LABELS, cameraState, type CameraState } from '@/lib/workspace'
import type { StreamResource } from '@/types/events'

export function useCameraState(stream: StreamResource) {
  const identities = useEventLiveKitLivePublisherIdentities()
  const live = identities.has(stream.attributes.participant_identity || '')
  const seen = useRef(false)
  useEffect(() => {
    if (live) seen.current = true
  }, [live])
  return cameraState(stream, live, seen.current)
}

/** Each connection state as a Play badge: green live only with video evidence, warning while connecting, error when
 *  offline. The glyph and the words carry the state with the colour. */
const STATUS_BADGES: Record<CameraState, { tone: BadgeTone; icon?: ReactElement }> = {
  invited: { tone: 'neutral', icon: <Video /> },
  connecting: { tone: 'warning', icon: <RotateCw /> },
  live: { tone: 'live' },
  reconnecting: { tone: 'warning', icon: <RotateCw /> },
  offline: { tone: 'error', icon: <VideoOff /> },
  ended: { tone: 'neutral', icon: <Check /> },
}

export function CameraStatus({
  state,
  label,
}: {
  state: CameraState
  label?: string
}) {
  const { tone, icon } = STATUS_BADGES[state]
  return (
    <Badge tone={tone} icon={icon} className="pw-status" data-state={state}>
      {label || CAMERA_STATE_LABELS[state]}
    </Badge>
  )
}

export function CameraPicture({
  stream,
  portal = false,
}: {
  stream: StreamResource
  portal?: boolean
}) {
  const state = useCameraState(stream)
  const waiting =
    stream.attributes.source_role === 'programme'
      ? 'Start streaming in OBS'
      : state === 'invited'
        ? 'Ready for your camera'
        : state === 'reconnecting'
          ? 'Waiting for the camera to return'
          : state === 'ended'
            ? 'Camera has stopped'
            : state === 'connecting'
              ? 'Connecting your preview'
              : 'Camera is offline'
  return (
    <StreamVideoSurface
      participantIdentity={stream.attributes.participant_identity}
      status={stream.attributes.status}
      routeStatus={stream.attributes.route_status}
      className={`pw-video ${portal ? 'pw-video--portal' : ''}`}
      fallback={
        state === 'connecting' || state === 'reconnecting' ? (
          <div role="status" aria-label={`${CAMERA_STATE_LABELS[state]} ${stream.attributes.name}`} aria-busy="true" className="absolute inset-0">
            <Skeleton className="h-full w-full rounded-none bg-neutral-8" />
          </div>
        ) : (
          <div className="pw-video-fallback">
            <Video size={portal ? 22 : 32} strokeWidth={1.5} />
            {!portal && <span>{waiting}</span>}
          </div>
        )
      }
    />
  )
}
